import React, { useState, useEffect, useRef } from 'react';
import { API_URL } from '../config/api';
import './ChatWidget.css';

interface Attachment {
  filename: string;
  originalname: string;
  path: string;
  mimetype: string;
  size: number;
}

interface Message {
  _id?: string;
  sender: 'creator' | 'admin';
  content: string;
  timestamp: string;
  read: boolean;
  attachments?: Attachment[];
}

interface ChatWidgetProps {
  collaborationId: string;
  userType: 'creator' | 'admin';
}

const ChatWidget: React.FC<ChatWidgetProps> = ({ collaborationId, userType }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // API 경로 결정 (크리에이터는 공개 API, 관리자는 인증 API)
  const getApiPath = (endpoint: string) => {
    const basePath = userType === 'creator'
      ? `${API_URL}/api/collaborations/public/${collaborationId}`
      : `${API_URL}/api/collaborations/${collaborationId}`;
    return `${basePath}${endpoint}`;
  };

  // Fetch messages
  const fetchMessages = async () => {
    try {
      const response = await fetch(getApiPath('/messages'));
      const data = await response.json();

      if (data.success) {
        setMessages(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  // Mark messages as read
  const markMessagesAsRead = async () => {
    try {
      // Mark messages from the OTHER party as read
      const otherSender = userType === 'creator' ? 'admin' : 'creator';

      await fetch(getApiPath('/messages/read'), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sender: otherSender })
      });
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  // Send message
  const sendMessage = async () => {
    if ((!newMessage.trim() && attachedFiles.length === 0) || sending) return;

    setSending(true);
    try {
      const formData = new FormData();
      formData.append('content', newMessage.trim() || '(File attached)');
      formData.append('sender', userType);

      // Append files
      attachedFiles.forEach(file => {
        formData.append('attachments', file);
      });

      const response = await fetch(getApiPath('/messages'), {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        setMessages(data.data || []);
        setNewMessage('');
        setAttachedFiles([]);
        scrollToBottom();
      }
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setAttachedFiles(prev => [...prev, ...files].slice(0, 5)); // Max 5 files
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Remove attached file
  const removeFile = (index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Initial load
  useEffect(() => {
    setLoading(true);
    fetchMessages().finally(() => setLoading(false));
  }, [collaborationId]);

  // Poll for new messages every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchMessages();
    }, 10000);

    return () => clearInterval(interval);
  }, [collaborationId]);

  // Mark messages as read when chat is opened
  useEffect(() => {
    if (isOpen) {
      markMessagesAsRead();
      scrollToBottom();
      inputRef.current?.focus();
    }
  }, [isOpen, messages]);

  // Auto scroll when new messages arrive
  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [messages.length]);

  // Count unread messages from the other party
  const unreadCount = messages.filter(
    msg => msg.sender !== userType && !msg.read
  ).length;

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="chat-widget">
      {!isOpen && (
        <button
          className="chat-toggle-btn"
          onClick={() => setIsOpen(true)}
          aria-label="Open chat"
        >
          💬
          {unreadCount > 0 && (
            <span className="chat-unread-badge">{unreadCount}</span>
          )}
        </button>
      )}

      {isOpen && (
        <div className="chat-window">
          <div className="chat-header">
            <div className="chat-header-content">
              <span className="chat-header-icon">💬</span>
              <span className="chat-header-title">Messages</span>
            </div>
            <button
              className="chat-close-btn"
              onClick={() => setIsOpen(false)}
              aria-label="Close chat"
            >
              ✕
            </button>
          </div>

          <div className="chat-messages">
            {loading ? (
              <div className="chat-loading">Loading messages...</div>
            ) : messages.length === 0 ? (
              <div className="chat-empty">
                <p>No messages yet.</p>
                <p className="chat-empty-subtitle">Start a conversation!</p>
              </div>
            ) : (
              messages.map((msg, index) => (
                <div
                  key={index}
                  className={`chat-message ${msg.sender === userType ? 'chat-message-sent' : 'chat-message-received'}`}
                >
                  <div className="chat-message-content">
                    <div className="chat-message-text">{msg.content}</div>

                    {/* Attachments */}
                    {msg.attachments && msg.attachments.length > 0 && (
                      <div className="chat-attachments">
                        {msg.attachments.map((attachment, attIndex) => (
                          <div key={attIndex} className="chat-attachment-item">
                            {attachment.mimetype.startsWith('image/') ? (
                              <img
                                src={`${API_URL}/api/collaborations/feedback-attachment/${attachment.filename}`}
                                alt={attachment.originalname}
                                className="chat-attachment-image"
                                onClick={() => window.open(`${API_URL}/api/collaborations/feedback-attachment/${attachment.filename}`, '_blank')}
                              />
                            ) : attachment.mimetype.startsWith('video/') ? (
                              <video
                                src={`${API_URL}/api/collaborations/feedback-attachment/${attachment.filename}`}
                                controls
                                className="chat-attachment-video"
                              />
                            ) : (
                              <a
                                href={`${API_URL}/api/collaborations/feedback-attachment/${attachment.filename}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="chat-attachment-link"
                              >
                                📎 {attachment.originalname}
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="chat-message-time">{formatTime(msg.timestamp)}</div>
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Attached files preview */}
          {attachedFiles.length > 0 && (
            <div className="chat-attached-files">
              {attachedFiles.map((file, index) => (
                <div key={index} className="chat-attached-file">
                  <span className="chat-attached-file-name">
                    {file.type.startsWith('image/') ? '🖼️' : file.type.startsWith('video/') ? '🎥' : '📎'} {file.name}
                  </span>
                  <span className="chat-attached-file-size">{formatFileSize(file.size)}</span>
                  <button
                    className="chat-attached-file-remove"
                    onClick={() => removeFile(index)}
                    aria-label="Remove file"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="chat-input-container">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,video/*"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
            <button
              className="chat-attach-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={sending || attachedFiles.length >= 5}
              aria-label="Attach file"
              title="Attach image or video"
            >
              📎
            </button>
            <textarea
              ref={inputRef}
              className="chat-input"
              placeholder="Type a message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              rows={1}
              disabled={sending}
            />
            <button
              className="chat-send-btn"
              onClick={sendMessage}
              disabled={(!newMessage.trim() && attachedFiles.length === 0) || sending}
              aria-label="Send message"
            >
              {sending ? '...' : '→'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatWidget;
